import { LanguageList } from '@util/i18n';
import { UpdateProfileArgs } from '@share/interfaces';
import { toast } from 'react-toastify';
import { useAPI } from 'src/hooks';
import { useDebouncedCallback } from 'use-debounce';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { user } from '@state/user';
import AssetsUpdate from './AssetsUpdate';
import ErrorMsg from '@com/utils/ErrorMsg';
import styled from '@emotion/styled';

const URL_PREFIX = 'https://';
const FACEBOOK_PREFIX = 'https://facebook.com/';
const TWITTER_PREFIX = 'https://twitter.com/';
const DISCORD_PREFIX = 'https://discord.com/';

const LanguageSelector = styled.select`
  line-height: 21px;
  border-radius: 8px;
  border: 1px solid rgba(138, 138, 160, 0.3);
`;

const ButtonGroup = styled.div`
  @media (max-width: 500px) {
    button {
      width: 100%;
      margin-right: 0px !important;
      margin-bottom: 2rem;
    }
  }
`;

const SocialInput = styled.div`
  display: flex;
  align-items: center;

  span {
    line-height: 22px;
    font-size: 14px;
    color: #8a8aa0;
    border-radius: 8px 0 0 8px;
    padding: 12px 15px 10px 20px;
    border: 1px solid rgba(138, 138, 160, 0.3);
  }

  input {
    border-radius: 0 8px 8px 0 !important;
  }
`;

function AccountGeneral(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isProfileNameValid, sendResetPasswordEmail, updateProfile } =
    useAPI();
  const { userData, isNoMetamask, setUser } = user.useContainer();
  const defaultData = useMemo(() => {
    if (userData) {
      const _user = userData.user;
      const socialLink = _user?.social_links;
      const result: UpdateProfileArgs = {
        name: _user?.name || '',
        bio: _user?.bio || '',
        language: _user?.language || LanguageList[0].key,
        facebookLink: socialLink[0]?.url?.replace(FACEBOOK_PREFIX, '') || '',
        twitterLink: socialLink[1]?.url?.replace(TWITTER_PREFIX, '') || '',
        discordLink: socialLink[2]?.url?.replace(DISCORD_PREFIX, '') || '',
        personalSite: socialLink[3]?.url?.replace(URL_PREFIX, '') || '',
      };
      return result;
    }
    return undefined;
  }, [userData]);

  const infoOnSubmit = useDebouncedCallback(
    async (inputs: UpdateProfileArgs) => {
      // No data update
      if (JSON.stringify(inputs) === JSON.stringify(defaultData)) {
        navigate('/profile/my');
        return;
      }

      const response = await updateProfile({
        ...inputs,
        personalSite: inputs.personalSite
          ? `${URL_PREFIX}${inputs.personalSite.replace(URL_PREFIX, '')}`
          : undefined,
        facebookLink: inputs.facebookLink
          ? `${FACEBOOK_PREFIX}${inputs.facebookLink.replace(
              FACEBOOK_PREFIX,
              ''
            )}`
          : undefined,
        twitterLink: inputs.twitterLink
          ? `${TWITTER_PREFIX}${inputs.twitterLink.replace(TWITTER_PREFIX, '')}`
          : undefined,
        discordLink: inputs.discordLink
          ? `${DISCORD_PREFIX}${inputs.discordLink.replace(DISCORD_PREFIX, '')}`
          : undefined,
      });

      // We must spreading the data since there have 'token' field
      if (userData) {
        setUser({
          ...userData,
          user: { ...userData?.user, ...response },
        });
        toast.success(t('toastMessage.profileUpdated'));
        navigate(`/profile/my`);
      }
    },
    500,
    { leading: true, trailing: false }
  );

  const changePassword = useDebouncedCallback(
    async () => {
      if (userData?.user?.email) {
        const hasSent = await sendResetPasswordEmail(userData.user.email);
        if (hasSent) {
          toast.success(t('toastMessage.changePasswordEmailSent'));
        }
      }
    },
    1000,
    {
      leading: true,
      trailing: false,
    }
  );

  const {
    register,
    handleSubmit: infoHandleSubmit,
    formState: { errors: infoErrors },
    reset,
  } = useForm<UpdateProfileArgs>();

  useEffect(() => {
    if (defaultData) reset(defaultData);
  }, [defaultData]);

  return (
    <div className="tf-create-item tf-section">
      <div className="themesflat-container">
        <div className="d-flex row justify-content-center">
          <div className="col-xl-3 col-lg-4 col-md-6 col-12">
            <AssetsUpdate />
          </div>
          <div className="col-xl-7 col-lg-8 col-md-12 col-12">
            <div className="form-upload-profile">
              <form
                className="form-profile"
                onSubmit={infoHandleSubmit(infoOnSubmit)}
              >
                <div className="form-infor-profile">
                  <div className="info-account">
                    <h4 className="title-create-item">
                      {t('editProfilePage.accountInfo')}
                    </h4>
                    <fieldset>
                      <h4 className="title-infor-account">
                        {t('editProfilePage.displayName')}
                      </h4>
                      <input
                        aria-required
                        aria-label={t('editProfilePage.namePlaceholder')}
                        placeholder={t('editProfilePage.namePlaceholder')}
                        type="text"
                        {...register('name', {
                          required: {
                            value: true,
                            message: t('errorMsg.editProfile.nameRequired'),
                          },
                          pattern: {
                            value: /^[\w-]([\s]?[\w-])*$/i,
                            message: t('errorMsg.editProfile.namePattern'),
                          },
                          validate: {
                            value: async (value) => {
                              return (
                                (await isProfileNameValid(value)) ||
                                t('errorMsg.editProfile.nameRepeated')
                              );
                            },
                          },
                        })}
                      />
                      <ErrorMsg errors={infoErrors} name="name" />
                    </fieldset>
                    <fieldset>
                      <h4 className="title-infor-account">
                        {t('editProfilePage.customURL')}
                      </h4>
                      <SocialInput>
                        <span>{URL_PREFIX}</span>
                        <input
                          aria-required
                          aria-label={t('editProfilePage.urlPlaceholder')}
                          placeholder={t('editProfilePage.urlPlaceholder')}
                          type="text"
                          {...register('personalSite', {
                            pattern: {
                              value:
                                /(?:www\.|(?!www)[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,})/gi,
                              message: t('errorMsg.editProfile.urlPattern'),
                            },
                          })}
                        />
                      </SocialInput>
                      <ErrorMsg errors={infoErrors} name="personalSite" />
                    </fieldset>
                    <fieldset>
                      <h4 className="title-infor-account">
                        {t('editProfilePage.bio')}
                      </h4>
                      <textarea
                        aria-required
                        aria-label={t('editProfilePage.biography')}
                        placeholder={t('editProfilePage.biography')}
                        rows={5}
                        {...register('bio')}
                      />
                      <ErrorMsg errors={infoErrors} name="bio" />
                    </fieldset>
                    <div>
                      <h4 className="title-infor-account mt-5">
                        {t('editProfilePage.language')}
                      </h4>
                      <LanguageSelector
                        className="p-3 w-100 text-center bg-transparent fs-16"
                        data-test="edit-prof-select-lang"
                        id="language"
                        {...register('language')}
                      >
                        {LanguageList?.map((item) => (
                          <option key={item.key} value={item.key}>
                            {item.name}
                          </option>
                        ))}
                      </LanguageSelector>
                      <ErrorMsg errors={infoErrors} name="language" />
                    </div>
                  </div>
                  <div className="info-social">
                    <h4 className="title-create-item">
                      {t('editProfilePage.yourSocialMedia')}
                    </h4>
                    <fieldset>
                      <h4 className="title-infor-account">
                        {t('editProfilePage.facebook')}
                      </h4>
                      <SocialInput>
                        <span>{FACEBOOK_PREFIX}</span>
                        <input
                          aria-label={t('editProfilePage.facebookPlaceholder')}
                          placeholder={t('editProfilePage.facebookPlaceholder')}
                          type="text"
                          {...register('facebookLink')}
                        />
                      </SocialInput>
                      <ErrorMsg errors={infoErrors} name="facebookLink" />
                    </fieldset>
                    <fieldset>
                      <h4 className="title-infor-account">
                        {t('editProfilePage.twitter')}
                      </h4>
                      <SocialInput>
                        <span>{TWITTER_PREFIX}</span>
                        <input
                          aria-label={t('editProfilePage.twitterPlaceholder')}
                          placeholder={t('editProfilePage.twitterPlaceholder')}
                          type="text"
                          {...register('twitterLink')}
                        />
                      </SocialInput>
                      <ErrorMsg errors={infoErrors} name="twitterLink" />
                    </fieldset>
                    <fieldset>
                      <h4 className="title-infor-account">
                        {t('editProfilePage.discord')}
                      </h4>
                      <SocialInput>
                        <span>{DISCORD_PREFIX}</span>
                        <input
                          aria-label={t('editProfilePage.discordPlaceholder')}
                          placeholder={t('editProfilePage.discordPlaceholder')}
                          type="text"
                          {...register('discordLink')}
                        />
                      </SocialInput>
                      <ErrorMsg errors={infoErrors} name="discordLink" />
                      {/* <Link className="connect" to="#">
                          <i className="icon-fl-vt" />
                          {t('editProfilePage.connectTo')} Discord
                        </Link> */}
                    </fieldset>
                  </div>
                </div>
                <ButtonGroup className="mt-5 w-full d-flex justify-content-between flex-wrap">
                  <button
                    className="tf-button-submit flex-grow-1 mr-5"
                    data-test="edit-prof-upd-prof-btn"
                    type="submit"
                  >
                    {t('editProfilePage.updateProfile')}
                  </button>
                  {isNoMetamask && (
                    <button
                      className="tf-button-submit flex-grow-1"
                      type="button"
                      onClick={() => {
                        changePassword();
                      }}
                    >
                      {t('editProfilePage.changePassword')}
                    </button>
                  )}
                </ButtonGroup>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AccountGeneral;
